import { computed, inject, Injectable, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { TuiAlertService } from '@taiga-ui/core';
import { NonNullableGenericState, User } from '@us/web/shared/data-access';
import { getErrorMessage, RouteUtil } from '@us/web/shared/utils';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { OtpProvider } from '../enums';
import { FbLoginResponse, LoginParams, LoginResponse } from '../models';
import { AuthService } from '../services/auth.service';
import { AuthActions } from './auth.actions';
import { selectAllowLoginPrepaidCard, selectAllowLoginViaOtp } from '@us/web/shop/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { setUser } from '@sentry/angular-ivy';
import { ENVIRONMENT } from '@us/app-config';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const FB: any;

export type LoginState = NonNullableGenericState<{
    phoneNumber: string | null;
    otpProvider: OtpProvider | null;
    otpSent: boolean;
    resetPinCode?: boolean;
    currentStep: 'phone-number' | 'verify-otp' | 'register' | 'prepaid-card';
    /**
     * Due to need to init FB SDK
     */
    allowLoginViaFacebook: boolean;
}>;

@Injectable()
export class LoginStore extends ComponentStore<LoginState> {
    private readonly authService = inject(AuthService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly navCtrl = inject(NavController);
    private readonly alertService = inject(TuiAlertService);
    private readonly translocoService = inject(TranslocoService);
    private readonly store = inject(Store);
    private readonly env = inject(ENVIRONMENT);

    readonly phoneNumber = this.selectSignal(s => s.data.phoneNumber || null) as Signal<string | null>;
    readonly otpProvider = this.selectSignal(s => s.data.otpProvider || null) as Signal<OtpProvider | null>;
    readonly isLoading = this.selectSignal(s => s.status === 'loading');
    readonly currentStep = this.selectSignal(s => s.data.currentStep);
    readonly allowLoginViaFacebook = this.selectSignal(s => s.data.allowLoginViaFacebook);
    readonly resetPinCode = this.selectSignal(s => s.data.resetPinCode);
    readonly allowLoginViaOtp = this.store.selectSignal(selectAllowLoginViaOtp);
    readonly allowLoginPrepaidCard = this.store.selectSignal(selectAllowLoginPrepaidCard);
    readonly otpSent = this.selectSignal(s => s.data.otpSent);

    readonly usePinCode = computed(() => this.otpProvider() === OtpProvider.PIN_CODE);

    constructor() {
        super(<LoginState>{
            status: 'pending',
            data: {
                phoneNumber: null,
                otpProvider: null,
                otpSent: false,
                currentStep: 'phone-number',
                allowLoginViaFacebook: false
            },
            error: null
        });
    }

    readonly setAllowLoginViaFacebook = this.updater(
        (state, allowLoginViaFacebook: boolean): LoginState => ({
            ...state,
            data: { ...state.data, allowLoginViaFacebook }
        })
    );

    readonly setCurrentStep = this.updater(
        (state, currentStep: LoginState['data']['currentStep']): LoginState => ({
            ...state,
            data: { ...state.data, currentStep }
        })
    );

    readonly verifyPhoneNumber = this.effect((params$: Observable<{ phoneNumber: string }>) =>
        params$.pipe(
            tap(() => {
                this.patchState(state => ({
                    ...state,
                    status: 'loading',
                    error: null
                }));
            }),
            switchMap(({ phoneNumber }) =>
                this.authService.verifyPhoneNumber({ phone_number: phoneNumber }).pipe(
                    tapResponse(
                        response => {
                            const data: Partial<LoginState['data']> = {
                                phoneNumber
                            };

                            if (response.account_exist) {
                                data.otpProvider = response.pin_code_exist
                                    ? OtpProvider.PIN_CODE
                                    : this.allowLoginViaOtp()
                                      ? OtpProvider.SMS
                                      : null;

                                if (response.pin_code_exist) {
                                    data.currentStep = 'verify-otp';
                                }
                            } else {
                                data.currentStep = 'register';
                            }

                            this.patchState(state => ({
                                ...state,
                                status: 'success',
                                data: {
                                    ...state.data,
                                    ...data
                                },
                                error: null
                            }));

                            if (data.otpProvider === OtpProvider.SMS) {
                                this.sendOtp({
                                    phoneNumber,
                                    otpProvider: OtpProvider.SMS
                                });
                            }
                        },
                        error => {
                            console.error(getErrorMessage(error));
                            this.patchState({
                                status: 'error',
                                error: getErrorMessage(error)
                            });
                        }
                    )
                )
            )
        )
    );

    readonly sendOtp = this.effect(
        (
            params$: Observable<{
                phoneNumber: string;
                otpProvider: OtpProvider;
                user?: Pick<User, 'email' | 'name' | 'birthday' | 'gender'>;
                onSuccess?: () => void;
            }>
        ) =>
            params$.pipe(
                tap(({ phoneNumber, otpProvider }) => {
                    this.patchState(state => ({
                        ...state,
                        status: 'loading',
                        data: {
                            ...state.data,
                            phoneNumber,
                            otpProvider: otpProvider,
                            otpSent: false
                        },
                        error: null
                    }));
                }),
                switchMap(({ phoneNumber, user, onSuccess }) =>
                    this.authService.sendOtp({ phone_number: phoneNumber, user }).pipe(
                        tapResponse(
                            async ({ otp_provider: otpProvider }) => {
                                this.patchState(state => ({
                                    ...state,
                                    status: 'success',
                                    data: { ...state.data, otpSent: true, currentStep: 'verify-otp', otpProvider },
                                    error: null
                                }));

                                onSuccess?.();
                            },
                            (error: HttpErrorResponse) => {
                                const message = getErrorMessage(error);
                                this.patchState(state => ({
                                    ...state,
                                    status: 'error',
                                    error: message
                                }));

                                this.alertService.open(message, { status: 'error' }).subscribe();
                            }
                        )
                    )
                ),
                catchError(error => {
                    console.error(error);
                    this.patchState({
                        status: 'error',
                        error: getErrorMessage(error)
                    });
                    return of(null);
                })
            )
    );

    readonly resendOtp = this.effect((params$: Observable<void>) =>
        params$.pipe(
            map(() => {
                return this.sendOtp({
                    phoneNumber: this.phoneNumber() as string,
                    otpProvider: this.otpProvider() as OtpProvider
                });
            })
        )
    );

    readonly forgotPassword = this.effect((params$: Observable<void>) =>
        params$.pipe(
            tap(() => {
                this.patchState(state => ({
                    ...state,
                    data: {
                        ...state.data,
                        resetPinCode: true,
                        otpProvider: OtpProvider.SMS
                    }
                }));
            }),
            map(() => {
                return this.sendOtp({
                    phoneNumber: this.phoneNumber() as string,
                    otpProvider: OtpProvider.SMS
                });
            })
        )
    );

    readonly verifyOtp = this.effect(
        (
            params$: Observable<{
                otp: string;
                onSuccess?: () => void;
                onFailed?: () => void;
            }>
        ) =>
            params$.pipe(
                tap(() => {
                    this.patchState({
                        status: 'loading',
                        error: null
                    });
                }),
                switchMap(({ otp, onSuccess, onFailed }) => {
                    const params = {
                        otp,
                        phone_number: this.phoneNumber() as string,
                        otp_provider: this.otpProvider() as OtpProvider,
                        reset_pin_code: this.resetPinCode()
                    };

                    return this.authService.verifyOtp(params).pipe(
                        tapResponse(
                            async response => {
                                await this.onLoginSuccess(response);
                                onSuccess?.();
                            },
                            error => {
                                this.onLoginError(error);
                                onFailed?.();
                            }
                        )
                    );
                })
            )
    );

    readonly loginViaFacebook = this.effect<void>((params$: Observable<void>) =>
        params$.pipe(
            tap(() => {
                FB.login(
                    (response: FbLoginResponse) => {
                        if (response.authResponse) {
                            this.loginViaSocial({
                                user: {
                                    provider: 'FACEBOOK',
                                    access_token: response.authResponse.accessToken,
                                    id: response.authResponse.userID
                                }
                            });
                        } else {
                            console.log('User cancelled login or did not fully authorize.');
                        }
                    },
                    { scope: 'public_profile,email' }
                );
            })
        )
    );

    private readonly loginViaSocial = this.effect(
        (
            params$: Observable<{
                user: LoginParams;
                onSuccess?: () => void;
            }>
        ) =>
            params$.pipe(
                tap(() => {
                    this.patchState({
                        status: 'loading',
                        error: null
                    });
                }),
                switchMap(({ user, onSuccess }) => {
                    return this.authService.login(user).pipe(
                        tapResponse(
                            response => {
                                onSuccess?.();
                                return this.onLoginSuccess(response);
                            },
                            error => this.onLoginError(error)
                        )
                    );
                })
            )
    );

    readonly loginPrepaidCard = this.effect(
        (
            params$: Observable<{
                data: {
                    card_id: string;
                    password: string;
                };
                onSuccess?: () => void;
            }>
        ) =>
            params$.pipe(
                tap(() => {
                    this.patchState({
                        status: 'loading',
                        error: null
                    });
                }),
                switchMap(({ data, onSuccess }) => {
                    return this.authService.login(data).pipe(
                        tapResponse(
                            response => {
                                onSuccess?.();
                                return this.onLoginSuccess(response);
                            },
                            error => this.onLoginError(error)
                        )
                    );
                })
            )
    );

    private async onLoginSuccess(response: LoginResponse) {
        this.patchState({
            status: 'success',
            data: {
                phoneNumber: null,
                otpProvider: null,
                otpSent: false,
                allowLoginViaFacebook: false,
                currentStep: this.allowLoginPrepaidCard() ? 'prepaid-card' : 'phone-number'
            },
            error: null
        });

        this.alertService
            .open(this.translocoService.translate('logged_in_successfully'), { status: 'success' })
            .subscribe();

        const { access_token, token_type, expires_in, api_domain, ...user } = response;
        this.store.dispatch(
            AuthActions.loginSuccess({
                user,
                token: { access_token, token_type, expires_in, api_domain }
            })
        );

        if (this.env.production) {
            setUser({
                id: user.id.toString(),
                username: user.username || user.phone,
                email: user.email || undefined
            });
        }

        const returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'];
        await this.navCtrl.navigateRoot(returnUrl || RouteUtil.getHomeRouteUrl());
    }

    private onLoginError(error: unknown) {
        const message = getErrorMessage(error);
        this.patchState({
            status: 'error',
            error: message
        });
        this.alertService.open(message, { status: 'error' }).subscribe();
    }
}
